import { useEffect, useRef } from 'react';

interface Speckle {
  x: number;
  y: number;
  vx: number; // velocity x
  vy: number; // velocity y
  radius: number;
  color: string;
  opacity: number;
}

/**
 * Brown-blue gradient base with animated white speckles
 * White speckles move at the same speed as tiger-lily animation
 * For events-page-hero section
 */
export const useBrownBlueSpeckleBackground = (containerRef: React.RefObject<HTMLElement | null>) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const specklesRef = useRef<Speckle[]>([]);
  const baseBlobsRef = useRef<Array<{ x: number; y: number; radius: number; color: string; opacity: number }>>([]);

  useEffect(() => {
    let initTimeout: NodeJS.Timeout;
    let resizeTimeout: NodeJS.Timeout;
    
    // Wait for next tick to ensure DOM is ready
    initTimeout = setTimeout(() => {
      const container = containerRef.current;
      const canvas = canvasRef.current;
      
      if (!container || !canvas) {
        console.warn('useBrownBlueSpeckleBackground: Container or canvas not found', { container: !!container, canvas: !!canvas });
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.warn('useBrownBlueSpeckleBackground: Could not get 2D context');
        return;
      }

      const rand = (min: number, max: number): number => {
        return Math.floor(Math.random() * (max - min)) + min;
      };

      const initPattern = (width: number, height: number) => {
        // STEP 1: Create brown-blue TEXTURED BASE (static)
        const baseColors = [
          '90, 75, 65',      // Medium brown
          '65, 85, 105',     // Slate blue
          '75, 70, 65',      // Dark brown
          '60, 75, 90'       // Blue-gray
        ];

        // Store base blobs (static)
        baseBlobsRef.current = [];
        for (let i = 0; i < 80; i++) {
          baseBlobsRef.current.push({
            x: Math.random() * width,
            y: Math.random() * height,
            radius: rand(20, 50),
            color: baseColors[rand(0, baseColors.length)],
            opacity: 0.15 + Math.random() * 0.15
          });
        }

        // STEP 2: Initialize SPECKLES with velocities (animated) - using #F5F5F0 (silky matte white)
        const speckleColors = [
          '245, 245, 240'    // #F5F5F0 - Silky matte white
        ];

        specklesRef.current = [];
        const speckleCount = 75; // Half of 150
        for (let i = 0; i < speckleCount; i++) {
          const sizeType = Math.random();
          let radius: number;
          if (sizeType < 0.5) {
            radius = rand(2, 4);   // Slightly larger small
          } else if (sizeType < 0.8) {
            radius = rand(4, 7);   // Medium
          } else {
            radius = rand(7, 12);  // Large
          }

          // Match tiger-lily animation speed (0.0012 * 40 = ~0.048 per frame)
          // Using similar velocity range as tiger-lily
          specklesRef.current.push({
            x: Math.random() * width,
            y: Math.random() * height,
            vx: (Math.random() - 0.5) * 0.048, // Matches tiger-lily speed
            vy: (Math.random() - 0.5) * 0.048, // Matches tiger-lily speed
            radius: radius,
            color: speckleColors[rand(0, speckleColors.length)],
            opacity: 0.25 // 25% transparent
          });
        }
      };

      const drawPattern = () => {
        const width = container.offsetWidth || 1200;
        const height = container.offsetHeight || 600;
        
        // Set canvas internal resolution to match container size
        canvas.width = width;
        canvas.height = height;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        // STEP 1: Create solid brown-blue BASE with gradient
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, 'rgb(85, 75, 70)');    // Brown
        gradient.addColorStop(0.5, 'rgb(70, 80, 95)');  // Blue-gray
        gradient.addColorStop(1, 'rgb(80, 70, 75)');    // Brown-gray
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        // Draw static base blobs
        baseBlobsRef.current.forEach(blob => {
          ctx.beginPath();
          ctx.fillStyle = `rgba(${blob.color}, ${blob.opacity})`;
          ctx.arc(blob.x, blob.y, blob.radius, 0, 2 * Math.PI, false);
          ctx.fill();
          ctx.closePath();
        });

        // Update and draw animated white speckles
        specklesRef.current.forEach(speckle => {
          // Update position
          speckle.x += speckle.vx;
          speckle.y += speckle.vy;

          // Wrap around edges
          if (speckle.x < 0) speckle.x = width;
          if (speckle.x > width) speckle.x = 0;
          if (speckle.y < 0) speckle.y = height;
          if (speckle.y > height) speckle.y = 0;

          // Draw white speckle with slight glow
          ctx.shadowBlur = 2;
          ctx.shadowColor = 'rgba(255, 255, 255, 0.5)';

          ctx.beginPath();
          ctx.fillStyle = `rgba(${speckle.color}, ${speckle.opacity})`;
          ctx.arc(speckle.x, speckle.y, speckle.radius, 0, 2 * Math.PI, false);
          ctx.fill();
          ctx.closePath();

          ctx.shadowBlur = 0; // Reset shadow
        });
      };

      const animate = () => {
        drawPattern();
        animationFrameRef.current = requestAnimationFrame(animate);
      };

      // Initialize pattern - ensure container has dimensions
      const initialize = () => {
        const width = container.offsetWidth || 1200;
        const height = container.offsetHeight || 600;
        
        console.log('useBrownBlueSpeckleBackground: Initializing', { width, height, containerHeight: container.offsetHeight });
        
        // Only initialize if container has valid dimensions
        if (width > 0 && height > 0) {
          initPattern(width, height);
          // Start animation
          animate();
        } else {
          // Retry initialization if container not ready yet
          console.log('useBrownBlueSpeckleBackground: Container not ready, retrying...', { width, height });
          requestAnimationFrame(initialize);
        }
      };
      
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(initialize);

      // Handle window resize
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const newWidth = container.offsetWidth || 1200;
          const newHeight = container.offsetHeight || 600;
          initPattern(newWidth, newHeight);
        }, 150);
      };

      window.addEventListener('resize', handleResize);
    }, 100); // Small delay to ensure DOM is ready
    
    // Cleanup for the effect
    return () => {
      clearTimeout(initTimeout);
      clearTimeout(resizeTimeout);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [containerRef]);

  return canvasRef;
};
